import React, { useEffect, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { Popover, Target, Trigger, useIsOpen, usePlacement } from '@accessible/popover';
import { isChrome } from '../../accessibleNavigation';

interface IPopoverComponentProps extends WithTranslation {
    children: any;
    icon?: boolean;
    triggerContent?: any;
    className?: string;
    tabIndex?: number;
    prefix?: string;
}

const PopoverBox = (props: { id: string | number; children: any }) => {
    const renderPlacement = usePlacement();
    const [left, setLeft] = useState<number | string>(0);
    const [top, setTop] = useState<number | string>(0);
    const open = useIsOpen();

    const styles = () => {
        setTimeout(function () {
            const arrow = document
                .getElementById(`popover_${props.id}`)
                ?.querySelector('.popover__arrow');
            const btnCoords = document
                .getElementById(`popover_${props.id}`)
                ?.querySelector('.popover__button')
                ?.getBoundingClientRect();
            const overlay = document
                .getElementById(`popover_${props.id}`)
                ?.querySelector('.popover-container__body') as HTMLElement;
            const overlayCoords = overlay?.getBoundingClientRect();

            if (btnCoords && overlayCoords && arrow) {
                // Hide arrow when zoomed to 150%
                if (Math.round(window.devicePixelRatio * 50) === 150) {
                    setTop('-1000px');
                } else if (overlayCoords.top > btnCoords.top) {
                    setLeft(Math.abs(btnCoords.left - overlayCoords.left) + 3);
                    setTop('-1px');
                } else if (overlayCoords.top < btnCoords.top) {
                    setLeft(Math.abs(btnCoords.left - overlayCoords.left) + 3);
                    setTop('100%');
                } else if (overlayCoords.left < btnCoords.left) {
                    setLeft('100%');
                    setTop(Math.abs(btnCoords.top - overlayCoords.top));
                } else if (overlayCoords.left > btnCoords.left) {
                    setLeft(0);
                    setTop(Math.abs(btnCoords.top - overlayCoords.top));
                }

                // Handle when the tooltip goes offscreen
                if (overlayCoords.top < 0) {
                    overlay.style.top = '0px';
                    overlay.style.overflow = 'auto';
                }
                if (overlayCoords.bottom > window.innerHeight) {
                    overlay.style.bottom = '0px';
                    overlay.style.overflow = 'auto';
                }
            }
        }, 50);
    };

    useEffect(() => {
        styles();
    }, [open]);

    return (
        <Target closeOnEscape>
            <div
                className="popover-container__body"
                role={'region'}
                aria-labelledby={`popover_${props.id}`}
            >
                <div
                    className={`popover__arrow--${renderPlacement} popover__arrow`}
                    style={{ left, top }}
                ></div>
                {props.children}
            </div>
        </Target>
    );
};

const getTextContentFromReactElement = (elem: React.ReactElement | string): string => {
    if (!elem) {
        return '';
    }
    if (typeof elem === 'string') {
        return elem;
    }
    const children = elem.props && elem.props.children;
    if (children instanceof Array) {
        return children.map(getTextContentFromReactElement).join('');
    }
    return getTextContentFromReactElement(children);
};

const getPopoverAriaLabel = (elem: React.ReactElement | string, prefix?: string): string => {
    return (prefix ?? '').addSpace().concat(getTextContentFromReactElement(elem));
}

const PopoverContainer = (props: IPopoverComponentProps) => {
    const { children, icon, triggerContent, className, tabIndex, prefix } = props;

    const [open, setOpen] = useState<boolean>(false);
    const [id] = useState<number>(Math.floor(Math.random() * Date.now()));

    /*
     * There is some aria-live and aria-label manipulation here to make sure we handle both VoiceOver on iOS and chrome NVDA
     * Doing a "setAttribute" programmatically trigger screen readers differently, so we need to tweak the native event.
     * While firefox and chrome will read a new aria-label, VO needs to have an aria-live to listen to it. BUT, if we add an aria-live to chrome, it will read the text 3 times for somes reasons
     */

    useEffect(() => {
        const popover = document.getElementById(`popover_${id}`);
        if (!isChrome()) {
            popover?.removeAttribute('aria-live');
        }
        if (open) {
            popover?.setAttribute('aria-label', getPopoverAriaLabel(children, prefix));
        } else {
            popover?.removeAttribute('aria-label');
        }
    }, [open]);

    useEffect(() => {
        setTimeout(() => {
            const popover = document.getElementById(`popover_${id}`);
            if (!isChrome()) {
                popover?.setAttribute('aria-live', 'polite');
            }
            popover?.setAttribute('aria-label', getPopoverAriaLabel(children, prefix));
            popover?.removeAttribute('aria-haspopup');
        }, 100);
    }, []);

    window.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
            setOpen(false);
        }
    });
    return (
        <Popover containPolicy={'flip'} open={open} repositionOnScroll repositionOnResize>
            <Trigger on="hover">
                <div
                    className={`popover-container ${className}`}
                    tabIndex={tabIndex ?? 0}
                    id={`popover_${id}`}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            setOpen(!open);
                        }
                    }}
                    onClick={(e) => {
                        e.preventDefault();
                        setOpen(!open);
                    }}
                    onBlur={(e) => {
                        e.preventDefault();
                        setOpen(false);
                        setTimeout(() => {
                            const popover = document.getElementById(`popover_${id}`);
                            popover?.setAttribute(
                                'aria-label',
                                getPopoverAriaLabel(children, prefix)
                            );
                            if (open) {
                                popover?.setAttribute('aria-expanded', 'true');
                            } else {
                                popover?.removeAttribute('aria-expanded');
                            }
                        }, 100);
                    }}
                    onFocus={(e) => {
                        e.preventDefault();
                        setOpen(true);
                    }}
                    onMouseOver={(e) => {
                        e.preventDefault();
                        setOpen(true);
                    }}
                    onMouseLeave={(e) => {
                        e.preventDefault();
                        setOpen(false);
                    }}
                    aria-label={getPopoverAriaLabel(children, prefix)}
                >
                    <a
                        href={'#'}
                        onClick={(e) => e.preventDefault()}
                        role={'button'}
                        aria-hidden
                        tabIndex={-1}
                        className={'popover__button standalone-link standalone-link--large'}
                    >
                        {icon && <div className={'icon-info'}></div>}
                        {!icon && triggerContent && (
                            <div dangerouslySetInnerHTML={{ __html: triggerContent }}></div>
                        )}
                    </a>
                    <PopoverBox aria-hidden id={id}>
                        {children}
                    </PopoverBox>
                </div>
            </Trigger>
        </Popover>
    );
};

export default withTranslation()(PopoverContainer);
